/* eslint-disable react/jsx-no-undef */
/* eslint-disable react/jsx-no-bind */
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FormikProvider, useFormik, Form } from 'formik';
import {
  Layout, Select, AutoComplete, Row, Col, Form as AntForm,
} from 'antd';
import { RightOutlined } from '@ant-design/icons';
import isEqual from 'lodash.isequal';
import FormControl from '../../lib/form-control';
import InputSelect from '../../lib/input-select';
import PageTitle from '../../lib/page-title';
import { useActions } from '../../providers/actions-provider/actions.context';
import { ACCOUNT_PATH, CHANGE_PASSWORD_PATH } from '../../routing/paths.constants';
import { useStore } from '../../../store';
import useGetAccountDetails from './use-getAccountDetails';
import PrimaryButton from '../../lib/primary-button';
import { createValidator } from '../../../utils/create-validator';
import { AccountDTO } from '../../../store/modules/user/dto/account.dto';

const Account = (): JSX.Element => {
  const [timeZonesList, setTimeZonesList] = useState<{ value: string, label: string }[]>([]);
  const {
    currentUser, tenants, groups,
    facilities, timeZones, authType,
  } = useStore();
  const authTypeValue = authType.data?.auth_type;
  const isNotNative = !!authTypeValue && authTypeValue !== 'NATIVE';
  useGetAccountDetails(isNotNative);
  const { userActions } = useActions();

  const groupsList = Object.values(groups.data);
  const defaultGroup = groupsList.find((el) => el.code === currentUser.data.group_code);

  useEffect(() => {
    setTimeZonesList(timeZones.data.map((t) => ({ value: t, label: t })));
  }, [timeZones]);

  const formik = useFormik({
    initialValues: {
      name: currentUser.data.name,
      firstname: currentUser.data.firstname,
      lastname: currentUser.data.lastname,
      email: currentUser.data.email,
      tenant: currentUser.data.tenant,
      role_id: currentUser.data.role_id,
      role_code: currentUser.data.role_code,
      group_code: currentUser.data.group_code,
      createdTime: currentUser.data.createdTime,
      timeZone: currentUser.data.timeZone,
    },
    onSubmit: async (values) => {
      const {
        role_id,
        tenant,
        group_code,
        name,
        firstname,
        lastname,
        email,
        createdTime,
        timeZone,
      } = values;
      const timeZoneAux = timeZone || '';
      await userActions.editCurrentUser(currentUser.data.id, {
        role_id,
        tenant,
        group_code,
        name,
        firstname,
        lastname,
        email,
        createdTime,
        timeZone: timeZoneAux,
      });
      await userActions.getCurrentUser(currentUser.data.id);
    },
    enableReinitialize: true,
    validate: (values: AccountDTO) => createValidator(AccountDTO, values),
  });
  const { setFieldValue, values, initialValues } = formik;

  function onTimeZoneSearch(searchText: string) {
    const trimmedSearchText = searchText.trim();
    if (trimmedSearchText) {
      const opts = (timeZones.data || []).filter(
        (timeZone) => (
          timeZone.toLowerCase().includes(trimmedSearchText.toLowerCase())),
      );
      setTimeZonesList(opts.map((t) => ({ value: t, label: t })));
    } else {
      setTimeZonesList(timeZones.data.map((t) => ({ value: t, label: t })));
    }
  }

  function switchRole(role?: string) {
    let returnedElement;
    switch (role) {
      case 'root':
        returnedElement = (
          <>
            <AntForm.Item label="Role">
              <Select
                size="large"
                className="ant__select formik-form__dropdown"
                defaultValue={currentUser.data.role || ''}
                disabled
              />
            </AntForm.Item>
            <InputSelect
              title="Tenant"
              defaultValue={currentUser.data.tenant || ''}
              fieldName="tenant"
              onChange={(value: string | number) => {
                setFieldValue('tenant', value);
              }}
              data={tenants.data}
            />
            {!isNotNative && (
              <InputSelect
                title="Facility"
                defaultValue={defaultGroup?.name || ''}
                fieldName="group_code"
                onChange={(value: string | number) => {
                  // eslint-disable-next-line array-callback-return
                  groupsList.find((el) => {
                    if (el.name === value) {
                      setFieldValue('group_code', el.code);
                    }
                  });
                }}
                data={facilities.data}
                allowEmpty
              />
            )}
          </>
        );
        break;
      case 'tenant_root':
        returnedElement = (
          <>
            <AntForm.Item label="Role">
              <Select
                size="large"
                className="ant__select formik-form__dropdown"
                defaultValue={currentUser.data.role || ''}
                disabled
              />
            </AntForm.Item>

            <InputSelect
              title="Tenant"
              defaultValue={currentUser.data.tenant || ''}
              fieldName="tenant"
              onChange={(value: string | number) => {
                setFieldValue('tenant', value);
              }}
              data={tenants.data}
              disabled
            />
            {!isNotNative && (
              <InputSelect
                title="Facility"
                defaultValue={defaultGroup?.name || ''}
                fieldName="group_code"
                onChange={(value: string | number) => {
                  // eslint-disable-next-line array-callback-return
                  groupsList.find((el) => {
                    if (el.name === value) {
                      setFieldValue('group_code', el.code);
                    }
                  });
                }}
                data={facilities.data}
                allowEmpty
              />
            )}
          </>
        );
        break;
      case 'tenant_admin':
      case 'tenant_user':
        returnedElement = (
          <>
            <AntForm.Item label="Role">
              <Select
                size="large"
                className="ant__select formik-form__dropdown"
                defaultValue={currentUser.data.role || ''}
                disabled
              />
            </AntForm.Item>
            {!isNotNative && (
              <InputSelect
                title="Facility"
                defaultValue={defaultGroup?.name || ''}
                fieldName="group_code"
                onChange={(value: string | number) => {
                  // eslint-disable-next-line array-callback-return
                  groupsList.find((el) => {
                    if (el.name === value) {
                      setFieldValue('group_code', el.code);
                    }
                  });
                }}
                data={facilities.data}
                allowEmpty
              />
            )}
          </>
        );
        break;
      default:
        break;
    }
    return returnedElement;
  }

  return (
    <Layout className="main-container user-details">
      <PageTitle title="Account" />
      <FormikProvider value={formik}>
        <Form className="formik-form" style={{ width: '100%' }}>
          <Row gutter={[10, 0]}>
            <Col style={{ minWidth: '280px' }}>
              <FormControl inputClassName="formik-form__input" name="name" label="Name" type="text" disabled />
              {!isNotNative && (
                <>
                  <FormControl inputClassName="formik-form__input" name="firstname" label="First Name" type="text" />
                  <FormControl inputClassName="formik-form__input" name="lastname" label="Last Name" type="text" />
                </>
              )}
              <FormControl inputClassName="formik-form__input" disabled={isNotNative} name="email" label="Email" type="text" />
            </Col>
            <Col style={{ minWidth: '280px' }}>
              {switchRole(currentUser.data.role_code)}
              {!isNotNative && (
                <AntForm.Item label="Time Zone" className="form-control">
                  <AutoComplete
                    options={timeZonesList}
                    value={values.timeZone || ''}
                    style={{ width: '100%' }}
                    onSearch={onTimeZoneSearch}
                    allowClear
                    placeholder="No Time Zone Selected"
                    onChange={(value:string) => {
                      setFieldValue('timeZone', value);
                    }}
                    onFocus={() => {
                      setTimeZonesList(timeZones.data.map((t) => ({ value: t, label: t })));
                    }}
                  />
                </AntForm.Item>
              )}
            </Col>
          </Row>
          <div className="formik-form__separator" style={{ width: '100%' }} />
          {!isNotNative && (
            <Link className="formik-form__reset-pass" to={`${ACCOUNT_PATH}${CHANGE_PASSWORD_PATH}`}>
              Password
              <RightOutlined />
            </Link>
          )}
          {!isNotNative && (
            <PrimaryButton type="submit" className="formik-form__submit" disabled={isEqual(values, initialValues)}>
              Save
            </PrimaryButton>
          )}
        </Form>
      </FormikProvider>
    </Layout>
  );
};

Account.displayName = 'Account';

export default Account;
